import React, { useState, useEffect } from 'react';
import { Sparkles, Brain, Film, Wand2, ArrowRight, CheckCircle2, Clock, Smartphone, Monitor, Megaphone, Target } from 'lucide-react';

const CAMPAIGN_PRESETS = [
  {
    id: 'meta_ads_15s',
    name: 'Meta Ads (Facebook/Instagram)',
    description: 'Anuncio de alta conversión para Reels y Stories',
    ratio: '9:16',
    defaultDuration: '15s',
    allowedDurations: ['15s', '30s'],
    badge: 'Recomendado para Ventas',
    icon: Megaphone
  },
  {
    id: 'meta_feed_1_1',
    name: 'Feed Cuadrado (Facebook / IG)',
    description: 'Anuncio para muro tradicional de noticias',
    ratio: '1:1',
    defaultDuration: '15s',
    allowedDurations: ['15s', '30s'],
    badge: 'Feed Estándar',
    icon: Target
  },
  {
    id: 'value_content_vertical',
    name: 'Contenido de Valor Vertical (Reels/TikTok)',
    description: 'Video educativo, tips o autoridad para marca personal',
    ratio: '9:16',
    defaultDuration: '30s',
    allowedDurations: ['30s', '60s'],
    badge: 'Marca Personal',
    icon: Smartphone
  },
  {
    id: 'value_content_horizontal',
    name: 'Contenido Horizontal (YouTube/Web)',
    description: 'Explicación detallada o video para página web',
    ratio: '16:9',
    defaultDuration: '60s',
    allowedDurations: ['30s', '60s'],
    badge: 'YouTube / Web',
    icon: Monitor
  }
];

export function CreativeDirectorModal({
  isOpen,
  onClose,
  onApplyCampaign,
  activeClient
}) {
  // Recordar última configuración elegida en localStorage
  const [selectedPresetId, setSelectedPresetId] = useState(() => {
    return localStorage.getItem('magnific_campaign_preset') || 'meta_ads_15s';
  });

  const activePreset = CAMPAIGN_PRESETS.find((p) => p.id === selectedPresetId) || CAMPAIGN_PRESETS[0];

  const [targetDuration, setTargetDuration] = useState(() => {
    return localStorage.getItem('magnific_campaign_duration') || activePreset.defaultDuration;
  });

  const [idea, setIdea] = useState('Sistema de confirmación y agendamiento automático por WhatsApp');
  const [targetAudience, setTargetAudience] = useState(() => {
    return activeClient?.niche || activeClient?.name || 'Clínicas Dentales y Ortodoncia';
  });
  const [tone, setTone] = useState(() => {
    return activeClient?.brandTone || 'persuasivo y profesional';
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);

  // Sincronizar campos cuando cambia activeClient
  useEffect(() => {
    if (activeClient) {
      if (activeClient.niche) setTargetAudience(activeClient.niche);
      if (activeClient.brandTone) setTone(activeClient.brandTone);
    }
  }, [activeClient]);

  // Cuando cambia el preset, actualizar en automático la duración recomendada y guardarlo
  const handleSelectPreset = (preset) => {
    setSelectedPresetId(preset.id);
    setTargetDuration(preset.defaultDuration);
    localStorage.setItem('magnific_campaign_preset', preset.id);
    localStorage.setItem('magnific_campaign_duration', preset.defaultDuration);
  };

  const handleSelectDuration = (dur) => {
    setTargetDuration(dur);
    localStorage.setItem('magnific_campaign_duration', dur);
  };

  if (!isOpen) return null;

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch('/api/creative/campaign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idea,
          targetAudience,
          tone,
          campaignType: activePreset.id,
          formatRatio: activePreset.ratio,
          targetDuration
        })
      });
      const data = await response.json();
      if (data.success && data.campaign) {
        setGeneratedResult(data.campaign);
      }
    } catch (err) {
      console.error('Error generando campaña:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApply = () => {
    if (generatedResult && onApplyCampaign) {
      onApplyCampaign({
        ...generatedResult,
        recommendedRatio: activePreset.ratio,
        recommendedDuration: targetDuration
      });
      onClose();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(5, 7, 12, 0.85)',
      backdropFilter: 'blur(16px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: '#111420',
        borderRadius: '16px',
        border: '1px solid rgba(255, 145, 1, 0.3)',
        boxShadow: '0 25px 60px -15px rgba(0,0,0,0.9), 0 0 40px rgba(255,145,1,0.15)',
        width: '100%',
        maxWidth: '740px',
        maxHeight: '92vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        padding: '24px'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ background: 'rgba(255,145,1,0.15)', padding: '8px', borderRadius: '10px', color: '#ff9101' }}>
              <Brain size={22} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 800, color: '#f8fafc', margin: 0 }}>
                  Estratega de Campañas & Director Creativo IA
                </h2>
                {activeClient && (
                  <span style={{ fontSize: '10px', background: 'rgba(168,85,247,0.2)', border: '1px solid rgba(168,85,247,0.4)', color: '#c084fc', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                    🏢 {activeClient.name}
                  </span>
                )}
              </div>
              <p style={{ fontSize: '11px', color: '#94a3b8', margin: '2px 0 0 0' }}>
                Investiga dolores de nicho, redacta guiones para Meta Ads y calibra la duración automáticamente
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '20px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        {/* 1. Selector Visual de Tipo de Campaña / Formato (Automatizado) */}
        <div>
          <label style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: '#ff9101', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <Target size={14} /> 1. Elige el Tipo de Campaña y Formato:
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px' }}>
            {CAMPAIGN_PRESETS.map((preset) => {
              const isSelected = preset.id === selectedPresetId;
              const IconComp = preset.icon;
              return (
                <div
                  key={preset.id}
                  onClick={() => handleSelectPreset(preset)}
                  style={{
                    padding: '12px',
                    borderRadius: '10px',
                    background: isSelected ? 'rgba(255, 145, 1, 0.12)' : '#161926',
                    border: isSelected ? '2px solid #ff9101' : '1px solid rgba(255,255,255,0.08)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <IconComp size={16} color={isSelected ? '#ff9101' : '#94a3b8'} />
                    <span style={{
                      fontSize: '10px',
                      fontWeight: 800,
                      background: isSelected ? '#ff9101' : 'rgba(255,255,255,0.06)',
                      color: isSelected ? '#000' : '#94a3b8',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {preset.ratio}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 800, color: isSelected ? '#fff' : '#e2e8f0' }}>
                    {preset.name}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', lineHeight: '1.3' }}>
                    {preset.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 2. Duración Automática / Calibrada */}
        <div style={{
          background: 'rgba(255, 145, 1, 0.05)',
          border: '1px solid rgba(255, 145, 1, 0.2)',
          borderRadius: '10px',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={16} color="#ff9101" />
            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#f8fafc' }}>
                Duración del Video: <span style={{ color: '#ff9101' }}>{targetDuration}</span>
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                {targetDuration === '15s' ? '🔥 Meta Ads: Máximo 40 palabras (Alta retención)' : '📚 Contenido de valor: 80 a 160 palabras'}
              </div>
            </div>
          </div>

          {/* Opciones rápidas de duración para este preset */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {activePreset.allowedDurations.map((dur) => (
              <button
                key={dur}
                type="button"
                onClick={() => handleSelectDuration(dur)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 800,
                  border: targetDuration === dur ? '1px solid #ff9101' : '1px solid rgba(255,255,255,0.1)',
                  background: targetDuration === dur ? '#ff9101' : '#161926',
                  color: targetDuration === dur ? '#000' : '#94a3b8',
                  cursor: 'pointer'
                }}
              >
                {dur}
              </button>
            ))}
          </div>
        </div>

        {/* Memoria Estratégica Previa de la Empresa Activa */}
        {activeClient?.strategicNotes && activeClient.strategicNotes.length > 0 && (
          <div style={{
            background: 'rgba(168, 85, 247, 0.05)',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            borderRadius: '10px',
            padding: '10px 14px'
          }}>
            <div style={{ fontSize: '10px', fontWeight: 800, color: '#c084fc', textTransform: 'uppercase', marginBottom: '4px' }}>
              🧠 Memoria Estratégica de {activeClient.name}:
            </div>
            <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '11px', color: '#94a3b8', lineHeight: '1.4' }}>
              {activeClient.strategicNotes.map((note, idx) => (
                <li key={idx}>{note}</li>
              ))}
            </ul>
          </div>
        )}

        {/* 3. Inputs de Nicho e Idea */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
              ¿A qué Nicho le vendemos?
            </label>
            <input
              type="text"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="Ej: Dentistas, Clínicas Estéticas, Talleres Mecánicos"
              style={{
                width: '100%',
                background: '#161926',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                padding: '8px 10px',
                color: '#f8fafc',
                fontSize: '12px',
                outline: 'none'
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
              Tono de Comunicación
            </label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              style={{
                width: '100%',
                background: '#161926',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                padding: '8px 10px',
                color: '#f8fafc',
                fontSize: '12px',
                outline: 'none'
              }}
            >
              <option value="persuasivo y profesional">Persuasivo & Ventas Directas</option>
              <option value="cinemático y aspiracional">Cinemático & Marca Exclusiva</option>
              <option value="dinámico estilo tiktok/reels">Dinámico & Rápido (Hook agresivo)</option>
              <option value="educativo y autoridad">Educativo & Autoridad Médica/Técnica</option>
            </select>
          </div>
        </div>

        {/* Idea / Oferta Central */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>
            ¿Qué solución u oferta les vas a ofrecer?
          </label>
          <input
            type="text"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            placeholder="Ej: CRM y bot de WhatsApp que confirma citas y no deja ir pacientes..."
            style={{
              width: '100%',
              background: '#161926',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '9px 12px',
              color: '#f8fafc',
              fontSize: '12px',
              outline: 'none'
            }}
          />
        </div>

        {/* Botón de Ejecución del Agente */}
        <button
          type="button"
          onClick={handleGenerate}
          disabled={isGenerating || !idea.trim()}
          style={{
            padding: '13px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #ff9101 0%, #a855f7 100%)',
            color: '#fff',
            fontWeight: 800,
            fontSize: '13px',
            border: 'none',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 4px 20px rgba(255,145,1,0.35)'
          }}
        >
          <Wand2 size={16} />
          {isGenerating
            ? 'Agente Investigando Nicho y Calibrando Guion...'
            : `🚀 Investigar Nicho y Generar Guion de ${targetDuration} (${activePreset.ratio})`}
        </button>

        {/* Resultado Generado con Diagnóstico de Nicho + Guion Calibrado */}
        {generatedResult && (
          <div style={{
            background: '#161926',
            borderRadius: '12px',
            border: '1px solid rgba(168,85,247,0.3)',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            animation: 'fadeIn 0.2s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', fontWeight: 800, color: '#c084fc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle2 size={16} color="#10b981" /> {generatedResult.title}
              </span>
              <span style={{ fontSize: '10px', color: '#ff9101', background: 'rgba(255,145,1,0.1)', border: '1px solid rgba(255,145,1,0.3)', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                {activePreset.ratio} • {targetDuration} • {generatedResult.wordCount || '~40'} palabras
              </span>
            </div>

            {/* Diagnóstico de Dolores de Nicho */}
            {generatedResult.nichePainPoints && (
              <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px 12px', borderRadius: '8px', borderLeft: '3px solid #c084fc' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#c084fc', textTransform: 'uppercase', marginBottom: '4px' }}>
                  🎯 Diagnóstico de Dolores del Nicho ({targetAudience}):
                </div>
                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '11px', color: '#94a3b8', lineHeight: '1.5' }}>
                  {generatedResult.nichePainPoints.map((dp, i) => (
                    <li key={i}>{dp}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Hook */}
            <div style={{ background: 'rgba(255,145,1,0.08)', padding: '8px 12px', borderRadius: '8px', borderLeft: '3px solid #ff9101' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#ff9101', textTransform: 'uppercase' }}>
                Gancho Disruptivo (0 a 3 seg):
              </div>
              <div style={{ fontSize: '12px', color: '#f8fafc', fontStyle: 'italic', marginTop: '2px' }}>
                "{generatedResult.hook}"
              </div>
            </div>

            {/* Guion Exacto */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
                <span>Guion Locutado para tu Voz Clonada:</span>
                <span style={{ color: '#10b981' }}>Tiempo calibrado para {targetDuration}</span>
              </div>
              <div style={{ fontSize: '12px', color: '#e2e8f0', lineHeight: '1.6', background: '#0f111a', padding: '10px', borderRadius: '8px' }}>
                {generatedResult.script}
              </div>
            </div>

            {/* Palabras Clave Karaoke */}
            {generatedResult.captions && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 700 }}>Subtítulos Neón:</span>
                {generatedResult.captions.map((cap, idx) => (
                  <span key={idx} style={{ fontSize: '10px', background: '#1e1b4b', color: '#a855f7', border: '1px solid rgba(168,85,247,0.3)', padding: '2px 6px', borderRadius: '4px', fontWeight: 800 }}>
                    {cap}
                  </span>
                ))}
              </div>
            )}

            {/* Botón Aplicar a Producción */}
            <button
              type="button"
              onClick={handleApply}
              style={{
                padding: '12px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                fontWeight: 800,
                fontSize: '13px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(16,185,129,0.3)',
                marginTop: '4px'
              }}
            >
              <span>Aplicar Guion, Tiempo ({targetDuration}) y Ratio ({activePreset.ratio}) a la Producción</span>
              <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
